import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, CircleNotch, DownloadSimple } from "@phosphor-icons/react";
import { pageQueryOptions } from "@/api-queries/pages";
import { BlogRenderer } from "@/components/blog/BlogRenderer";
import { DocumentRenderer } from "@/components/documents/DocumentRenderer";
import { Checkbox } from "@/components/ui/checkbox";
import { PayoutMethodField } from "@/components/enrollment/PayoutMethodField";
import { BillingIdentityFields, type BillingIdentityValue } from "@/components/enrollment/BillingIdentityFields";
import { formatDate } from "@/lib/format";
import { fillPlaceholders } from "@/lib/documents/fill-placeholders";
import { therapistPartyValues } from "@/lib/documents/therapist-party";
import { apiUrl } from "@/lib/api-url";
import type { PayoutMethod } from "@/types";
import type { TherapistContract } from "@/types/document";
import "@/components/blog/editor.css";

const TERMS_SLUG = "uslovi-koristenja";

interface Props {
  /** Ugovor o saradnji (aktivni `ugovor_terapeut`); izostavljen kad admin nema aktivan ugovor. */
  contract?: {
    data: TherapistContract | null | undefined;
    isLoading: boolean;
    agreed: boolean;
    onAgreedChange: (value: boolean) => void;
  };
  /** Način isplate i uslovi korištenja; samo za migrirane terapeute koji ih još nisu prihvatili. */
  billingTerms?: {
    accepted: boolean;
    payoutMethod: PayoutMethod;
    onPayoutMethodChange: (method: PayoutMethod) => void;
    billing: BillingIdentityValue;
    onBillingChange: (value: BillingIdentityValue) => void;
    isVolunteer: boolean;
    agreed: boolean;
    onAgreedChange: (value: boolean) => void;
  };
}

/**
 * Sve što terapeut potpisuje, na jednom ekranu: podaci za isplatu, ugovor o
 * saradnji i uslovi korištenja, sa potvrdama na dnu. Prihvatanja se i dalje
 * bilježe odvojeno (ugovor uz tačnu verziju u `document_acceptances`, uslovi kao
 * `termsAcceptedAt`); spojen je samo prikaz.
 */
export function AgreementStep({ contract, billingTerms }: Props) {
  const { t, i18n } = useTranslation();
  const showTerms = Boolean(billingTerms && !billingTerms.accepted);
  const { data: terms, isLoading: termsLoading } = useQuery({
    ...pageQueryOptions(TERMS_SLUG, i18n.language),
    enabled: showTerms,
  });

  const contractData = contract?.data;
  const contractAccepted = Boolean(contractData?.acceptedAt);
  // Dok terapeut kuca podatke za isplatu, ugovorna strana u ugovoru se popunjava uživo
  // istim pravilom koje server koristi za PDF (therapistPartyValues).
  const contractContent = useMemo(() => {
    if (!contractData) return null;
    if (!billingTerms || billingTerms.accepted) return contractData.contentJson;
    const values = {
      ...contractData.values,
      ...therapistPartyValues({
        name: contractData.values["terapeut.ime"] ?? "",
        payoutMethod: billingTerms.payoutMethod,
        ...billingTerms.billing,
      }),
    };
    return fillPlaceholders(JSON.parse(contractData.templateJson), values) as object;
  }, [contractData, billingTerms?.accepted, billingTerms?.payoutMethod, billingTerms?.billing]);
  let section = 0;
  const heading = (text: string) => {
    section += 1;
    return (
      <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
        {section}. {text}
      </h3>
    );
  };

  return (
    <div className="space-y-8">
      {billingTerms && !billingTerms.accepted && (
        <section className="space-y-4">
          {heading("Podaci za isplatu")}
          <PayoutMethodField value={billingTerms.payoutMethod} onChange={billingTerms.onPayoutMethodChange} />
          <BillingIdentityFields
            payoutMethod={billingTerms.payoutMethod}
            isVolunteer={billingTerms.isVolunteer}
            value={billingTerms.billing}
            onChange={billingTerms.onBillingChange}
          />
        </section>
      )}

      {contract && (
        <section className="space-y-3">
          {heading("Ugovor o poslovnoj saradnji")}
          {contract.isLoading ? (
            <div className="flex justify-center py-12">
              <CircleNotch className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : !contractData ? (
            <p className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
              Ugovor trenutno nije dostupan. Pokušaj ponovo kasnije ili se javi podršci.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  {contractData.title}, verzija {contractData.version}, ažurirano {formatDate(contractData.updatedAt)}
                </p>
                <a
                  href={apiUrl("/api/therapist/contract.pdf")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <DownloadSimple className="size-4" />
                  Preuzmi PDF
                </a>
              </div>
              <div className="max-h-80 overflow-y-auto rounded-xl border border-border bg-card p-5 text-sm">
                <DocumentRenderer content={contractContent ?? contractData.contentJson} className="ProseMirror text-sm" />
              </div>
            </>
          )}
        </section>
      )}

      {showTerms && (
        <section className="space-y-3">
          {heading(t("onboarding.billingTerms.termsTitle", { defaultValue: "Uslovi korištenja platforme" }))}
          <div className="max-h-64 overflow-y-auto rounded-xl border border-border bg-card p-4 text-sm">
            {termsLoading ? (
              <div className="space-y-3">
                <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-3 w-full animate-pulse rounded bg-muted" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
              </div>
            ) : terms?.content ? (
              <BlogRenderer content={terms.content} className="ProseMirror" />
            ) : (
              <p className="text-muted-foreground">
                {t("onboarding.billingTerms.termsUnavailable", {
                  defaultValue: "Uslovi trenutno nisu dostupni. Pokušaj ponovo kasnije.",
                })}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="space-y-3 rounded-xl border border-primary/25 bg-primary/[0.04] p-4">
        <h3 className="text-sm font-semibold text-foreground">Potvrda</h3>
        {contract && contractData && (
          contractAccepted ? (
            <AcceptedLine>
              Ugovor si prihvatio/la {formatDate(contractData.acceptedAt!)}
              {contractData.acceptedVersion ? ` (verzija ${contractData.acceptedVersion})` : ""}.
            </AcceptedLine>
          ) : (
            <AgreeBox checked={contract.agreed} onChange={contract.onAgreedChange}>
              Pročitao/la sam Ugovor o poslovnoj saradnji i prihvatam ga.
            </AgreeBox>
          )
        )}
        {billingTerms && (
          billingTerms.accepted ? (
            <AcceptedLine>Uslovi korištenja su prihvaćeni.</AcceptedLine>
          ) : (
            <AgreeBox checked={billingTerms.agreed} onChange={billingTerms.onAgreedChange}>
              {t("onboarding.billingTerms.agree", { defaultValue: "Slažem se sa uslovima korištenja platforme." })}
            </AgreeBox>
          )
        )}
      </section>
    </div>
  );
}

function AgreeBox({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-card p-3">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" />
      <span className="text-sm text-foreground">{children}</span>
    </label>
  );
}

function AcceptedLine({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-lg bg-card p-3 text-sm text-foreground">
      <CheckCircle weight="fill" className="mt-0.5 size-4 shrink-0 text-primary" />
      <span>{children}</span>
    </p>
  );
}
